import type { SyncToCrmResult } from './CompanyDossier';

/**
 * The run-candidate reads and writes a screen needs to show a run's companies
 * and act on them: the run's own list (WEB-10892) and the chat's company cards
 * (WEB-14734). State stays with the caller; these only talk to the API.
 */

/** The body of a response, or a rejection when the API answered with an error status. */
async function readOk(res: Response, what: string): Promise<any> {
	if (!res.ok) throw new Error(`${what} failed (HTTP ${res.status})`);
	return res.json();
}

/** A run's candidates, ranked, as `GET /prospectingAgentRuns/:id/candidates` returns them. */
export async function fetchRunCandidates<C = Record<string, any>>(runId: number): Promise<C[]> {
	const data = await readOk(await fetch(`/api/v2/prospectingAgentRuns/${runId}/candidates`), 'candidates read');
	return Array.isArray(data) ? data : data?.data ?? [];
}

/** Whether a run scored its companies: an enrich run skips scoring (WEB-9697), so its stored 0 is no score. */
export async function fetchRunIsScored(runId: number): Promise<boolean> {
	const data = await readOk(await fetch(`/api/v2/prospectingAgentRuns/${runId}`), 'run read');
	return !(data?.data ?? data)?.config?.skipScoring;
}

/** Imports the companies into the CRM; resolves with the import result rows by prospectingId. */
export async function importRunCandidates(
	runId: number,
	prospectingIds: string[],
	contactNames?: string[]
): Promise<Map<string, any>> {
	const res = await fetch('/api/v2/prospectingAgentRuns/import', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ prospectingIds, runId, contactNames })
	});
	const body = await readOk(res, 'import');
	const results: any[] = (body?.data ?? body)?.results ?? [];
	const importedById = new Map<string, any>();
	for (const r of results) {
		if (r?.prospectingId && r?.clientId) importedById.set(r.prospectingId, r);
	}
	return importedById;
}

/** Syncs a company onto a CRM account, or creates a new one; the dossier reports the counts to the rep. */
export async function syncRunCandidate(
	runId: number,
	prospectingId: string,
	clientId: number,
	mode: 'sync' | 'new',
	contactNames?: string[]
): Promise<SyncToCrmResult> {
	const res = await fetch('/api/v2/prospectingAgentRuns/sync', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ prospectingId, clientId, mode, runId, contactNames })
	});
	const json = await readOk(res, 'sync');
	return (json?.data ?? json) as SyncToCrmResult;
}

// Optimistically flag freshly-imported candidates as in-CRM. The candidates
// re-fetch reads Elasticsearch, which indexes the just-created client
// asynchronously, so it can briefly still report inCRM=false (WEB-7709); the
// optimistic client name also keeps the badge from reading "In CRM as
// undefined" until a reload (WEB-8029). `importedById` maps prospectingId → the
// matching import result row ({ clientId, ... }).
export const markImportedWith = (importedById: Map<string, any>) => (c: any) => {
	const hit = importedById.get(c.prospectingId);
	if (!hit) return c;
	return {
		...c,
		inCRM: true,
		crmClient: c.crmClient ?? {
			id: hit.clientId,
			name: c.name ?? '',
			matchReason: c.orgNumber || c.orgNo ? 'orgNo' : 'domain'
		}
	};
};
